import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { CustomTable, InputControl, usePermission } from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import { Clear } from "@mui/icons-material";
import editIcon from "../../../assets/images/Icons/editIcon.png";
import SaveIcon from "../../../assets/images/Icons/ic_save.svg";
import AlertIcon from "../../../assets/images/Icons/icAlert.svg";
import { useStoreStockSettings } from "../hooks";
import { CustomeDialog } from "../../../shared";
import { StockBulkUpdateTrigger } from "./StockBulkUpdateTrigger";

const columnHelper = createColumnHelper();

const visibilityOptions = [
  { label: "Visible", value: 1 },
  { label: "Hidden", value: 0 },
];

export const StoreStocksSettings = () => {
  const {
    tableId,
    storeStockItems,
    storeThresholds,
    isLoading,
    onSearchChange,
    onVisibilityChange,
    editingRowId,
    itemEdits,
    validationErrors,
    isSaving,
    onEdit,
    onCancel,
    onThresholdChange,
    onVisibilityToggle,
    visibilityConfirm,
    confirmVisibilityToggle,
    cancelVisibilityToggle,
    isVisibilityUpdating,
    isBulkVisibilityUpdating,
    selectionState,
    onBulkUpdateConfirm,
    updateBulkUpdateRowSelection,
  } = useStoreStockSettings();
  const isStockTakeCreatable = usePermission("stock-take-create");

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "index",
        header: ({ table }) => (
          <Checkbox
            size="small"
            sx={{ padding: 0 }}
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="small"
            sx={{ padding: 0 }}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        meta: { width: "50px", align: "center" },
      }),
      columnHelper.accessor("Store", {
        header: "Store",
        accessorFn: (row) => row.store_name || "-",
      }),
      columnHelper.accessor("threshold_unit", {
        header: () => (
          <Stack direction="column">
            <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
              Threshold
            </Typography>
            <Stack direction="row" gap="90px">
              <Typography
                sx={{ fontSize: "11px", fontWeight: 500, color: "#8E99AC" }}
              >
                Base Qty
              </Typography>
              <Typography
                sx={{ fontSize: "11px", fontWeight: 500, color: "#8E99AC" }}
              >
                Tolerance
              </Typography>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        meta: {
          width: "300px",
          borderLeft: "1px solid #DFE8F2",
          borderRight: "1px solid #DFE8F2",
        },
        cell: ({ row, table }) => {
          const {
            editingRowId: rowEditId,
            itemEdits: edits,
            validationErrors: errors,
            onThresholdChange: onThresholdCh,
          } = table.options.meta;
          const isEditing = rowEditId === row.original.id;
          const thresholdUnit =
            edits[row.original.id]?.threshold_unit ??
            row.original.threshold_qty;
          const thresholdPct =
            edits[row.original.id]?.threshold_percentage ??
            row.original.threshold_percentage;
          const qtyError = errors[row.original.id]?.threshold_unit;
          const pctError = errors[row.original.id]?.threshold_percentage;
          const {
            onEdit: onEditRow,
            onCancel: onCancelRow,
            isSaving: saving,
          } = table.options.meta;
          const hasError = !!pctError || !!qtyError;
          return (
            <Stack
              direction="row"
              gap="8px"
              alignItems="center"
              sx={{
                backgroundColor: isEditing ? "#DFE8F2" : "#F2F7FC",
                padding: "6px 8px",
                margin: "-8px",
              }}
            >
              <Stack direction="column" mb="auto">
                <OutlinedInput
                  type="number"
                  size="small"
                  value={thresholdUnit}
                  disabled={!isEditing}
                  error={!!qtyError}
                  onChange={(e) =>
                    onThresholdCh?.(
                      row.original.id,
                      "threshold_unit",
                      e.target.value,
                    )
                  }
                  sx={{
                    minWidth: "120px",
                    height: "36px",
                    backgroundColor: isEditing ? "#fff" : "transparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "neutral.10",
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#DFE8F2",
                    },
                    "& input": { padding: "8px 12px", fontSize: "16px" },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      { WebkitAppearance: "none", margin: 0 },
                    "& input[type=number]": { MozAppearance: "textfield" },
                  }}
                />
                {qtyError && (
                  <FormHelperText error sx={{ mx: 0, fontSize: "10px" }}>
                    {qtyError}
                  </FormHelperText>
                )}
              </Stack>
              <Stack direction="column" mb="auto">
                <OutlinedInput
                  type="number"
                  size="small"
                  value={thresholdPct}
                  disabled={!isEditing}
                  error={!!pctError}
                  onChange={(e) =>
                    onThresholdCh?.(
                      row.original.id,
                      "threshold_percentage",
                      e.target.value,
                    )
                  }
                  inputProps={{ style: { MozAppearance: "textfield" } }}
                  endAdornment={
                    <InputAdornment
                      position="end"
                      sx={{
                        backgroundColor: "#E6EEF7",
                        padding: "6px 8px",
                        margin: "0 -14px 0 0",
                        height: "100%",
                        maxHeight: "none",
                        borderLeft: "1px solid #DFE8F2",
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#8E99AC",
                          fontWeight: 500,
                        }}
                      >
                        %
                      </Typography>
                    </InputAdornment>
                  }
                  sx={{
                    minWidth: "120px",
                    height: "36px",
                    backgroundColor: isEditing ? "#fff" : "transparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "neutral.10",
                    },
                    "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#DFE8F2",
                    },
                    "& input": { padding: "8px 12px", fontSize: "16px" },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      { WebkitAppearance: "none", margin: 0 },
                    "& input[type=number]": { MozAppearance: "textfield" },
                  }}
                />
                {pctError && (
                  <FormHelperText error sx={{ mx: 0, fontSize: "10px" }}>
                    {pctError}
                  </FormHelperText>
                )}
              </Stack>
              {isStockTakeCreatable ? (
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    borderColor: isEditing ? "#B0BEC5" : "#DFE8F2",
                  }}
                />
              ) : null}

              {isEditing ? (
                <>
                  <Tooltip title="Save" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onEditRow?.(row.original)}
                      disabled={saving || hasError}
                    >
                      {saving ? (
                        <CircularProgress size={18} color="primary" />
                      ) : (
                        <Box
                          component="img"
                          src={SaveIcon}
                          height={20}
                          width={20}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onCancelRow?.(row.original.id)}
                      disabled={saving}
                    >
                      <Clear fontSize="small" color="error" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : isStockTakeCreatable ? (
                <IconButton
                  size="small"
                  onClick={() => onEditRow?.(row.original)}
                >
                  <Box component="img" src={editIcon} height={20} width={20} />
                </IconButton>
              ) : null}
            </Stack>
          );
        },
      }),
      columnHelper.accessor("visibility", {
        header: "Visibility",
        meta: { width: "80px", align: "right", pl: 2 },
        cell: ({ row, table }) => {
          const { itemEdits: edits, onVisibilityToggle: onVisTog } =
            table.options.meta;
          const isVisible =
            edits[row.original.id]?.visibility ?? row.original.visibility;
          return (
            <Switch
              size="small"
              checked={isVisible === 1}
              onChange={() => onVisTog?.(row.original.id, isVisible)}
              disabled={!isStockTakeCreatable}
            />
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeThresholds, editingRowId],
  );

  return (
    <>
      <CustomTable
        defaultData={storeThresholds || []}
        columns={columns}
        isLoading={isLoading}
        showSearchBar
        disablePagination={true}
        tableMeta={{
          editingRowId,
          itemEdits,
          validationErrors,
          isSaving,
          onThresholdChange,
          onVisibilityToggle,
          onEdit,
          onCancel,
        }}
        resetRowSelection={selectionState.resetSelection}
        onChangeRowSelection={(selectedRows) => {
          updateBulkUpdateRowSelection(selectedRows);
        }}
        searchText={storeStockItems?.search || ""}
        onSearchChange={onSearchChange}
        tableId={tableId}
        rightContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "6px",
              width: "100%",
              justifyContent: "flex-end",
            }}
          >
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={visibilityOptions}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{ width: "130px", height: "36px" }}
              value={storeStockItems?.data?.visibility}
              onChange={onVisibilityChange}
              renderInput={(params) => (
                <TextField {...params} placeholder="Visibility" />
              )}
            />

            <StockBulkUpdateTrigger
              disabledOptions={selectionState.selectedItems.length === 0}
              loading={isBulkVisibilityUpdating}
              onConfirmBulkUpdateToggle={onBulkUpdateConfirm}
            />
          </Stack>
        }
        enableTableContainerPaddingBottom
      />
      <CustomeDialog
        open={!!visibilityConfirm}
        handleClose={cancelVisibilityToggle}
        closeIcon
        divider={false}
        title={
          <Stack direction="row" alignItems="center" gap="8px" p="16px">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: "6px",
                backgroundColor: "#FFEDED",
                borderRadius: "50%",
              }}
            >
              <img src={AlertIcon} alt="icon" />
            </Box>
            <Typography fontWeight={600} fontSize="20px">
              {visibilityConfirm?.newVisibility === 0
                ? "Hide this item from stocktake?"
                : "Show this item in stocktake?"}
            </Typography>
          </Stack>
        }
        content={
          <Box sx={{ px: "24px", pb: "8px" }}>
            <Typography sx={{ fontSize: "14px", color: "#485364" }}>
              {visibilityConfirm?.newVisibility === 1
                ? `This item will appear in the ${visibilityConfirm?.item?.store_name} store’s stocktake. Do you want to continue?`
                : `This item will be removed from the ${visibilityConfirm?.item?.store_name} store’s stocktake. Do you want to continue?`}
            </Typography>
          </Box>
        }
        footer={{
          display: true,
          cancelButton: "No",
          confirmButton: "Yes",
          onCancel: cancelVisibilityToggle,
          onConfirm: confirmVisibilityToggle,
          isDisabled: isVisibilityUpdating,
        }}
      />
    </>
  );
};
